// RTK Query endpoints for the campaigns feature.
//
// EVERY endpoint below is defined against its final backend shape (path + params
// + response). Today, `queryFn` implementations run against the in-memory
// `mockBackend`. When the backend ships:
//   1. Replace each `queryFn` with `query: ...` that points at the path below.
//   2. Delete the `mockBackend` import at the bottom of this file.
//   3. Leave `providesTags` / `invalidatesTags` / `onQueryStarted` untouched.
//
// Endpoint paths mirror docs/crm-campaings/BACKEND-REQUIREMENTS.md. Where the
// doc specifies `POST /api/campaign { method, data }` RPC-style, we surface
// that as `buildRequest(method, data)` so the queryFn and the eventual real
// query share a single body shape.

import { rootApi } from '../../services/rootApi';
import * as mockBackend from './mockBackend';

// RPC-style request helper — mirrors Coursio's existing pattern (see
// `POST /api/campaign` in BACKEND-REQUIREMENTS.md). All campaign mutations go
// through a single endpoint with `{ method, data }` in the body.
const buildRequest = (method, data) => ({
  url: '/api/campaign',
  method: 'POST',
  body: { method, data },
});

// Simulated latency for realistic UX during mock-backed development.
const delay = (ms = 250) => new Promise((res) => setTimeout(res, ms));

export const campaignsApi = rootApi.injectEndpoints({
  endpoints: (builder) => ({
    // --- Queries ------------------------------------------------------------

    listCampaigns: builder.query({
      // Real: query: ({ filters, page, includeDeleted }) => ({ url: '/api/campaigns', params: { ... } })
      queryFn: async (args = {}) => {
        await delay();
        return { data: mockBackend.listCampaigns(args) };
      },
      providesTags: (result) => [
        { type: 'CampaignList', id: 'LIST' },
        ...(result?.items ?? []).map((c) => ({ type: 'Campaign', id: c.id })),
      ],
    }),

    getCampaign: builder.query({
      // Real: query: (id) => `/api/campaigns/${id}`
      queryFn: async (id) => {
        await delay();
        const c = mockBackend.getCampaign(id);
        return c ? { data: c } : { error: { status: 404, data: 'Campaign not found' } };
      },
      providesTags: (result, error, id) => [{ type: 'Campaign', id }],
    }),

    getCampaignStats: builder.query({
      // Real: query: (dateRange) => ({ url: '/api/campaigns/stats', params: dateRange })
      queryFn: async (dateRange) => {
        await delay();
        return { data: mockBackend.getStats(dateRange) };
      },
      providesTags: [{ type: 'CampaignStats', id: 'SUMMARY' }],
    }),

    previewAudience: builder.query({
      // Real: query: ({ filters, excludeUserIds }) => ({ url: '/api/campaigns/audience/preview', method: 'POST', body: { ... } })
      queryFn: async (args) => {
        await delay(200);
        return { data: mockBackend.previewAudience(args) };
      },
      providesTags: [{ type: 'AudiencePreview', id: 'CURRENT' }],
      keepUnusedDataFor: 60,
    }),

    getCampaignRecipients: builder.query({
      // Real: query: ({ id, page, status }) => ({ url: `/api/campaigns/${id}/recipients`, params: { ... } })
      queryFn: async (args) => {
        await delay();
        return { data: mockBackend.getRecipients(args) };
      },
      providesTags: (result, error, { id }) => [{ type: 'CampaignRecipients', id }],
    }),

    // Network-upsell placement endpoints — consumed by student-facing NetworkUpsells.js.
    // Gated at call site via `networkUpsells.useLiveCampaigns` feature flag.
    getActiveUpsells: builder.query({
      // Real: query: () => '/api/campaigns/upsells/active'
      queryFn: async () => {
        await delay();
        return { data: mockBackend.getActiveUpsells() };
      },
      providesTags: [{ type: 'UpsellPlacement', id: 'ACTIVE' }],
    }),

    // --- Mutations ----------------------------------------------------------

    createCampaign: builder.mutation({
      // Real: query: (data) => buildRequest('create', data)
      queryFn: async (data) => {
        await delay(400);
        return { data: mockBackend.createCampaign(data) };
      },
      invalidatesTags: [{ type: 'CampaignList', id: 'LIST' }, { type: 'CampaignStats', id: 'SUMMARY' }],
    }),

    updateCampaign: builder.mutation({
      // Real: query: ({ id, ...patch }) => buildRequest('update', { id, ...patch })
      queryFn: async ({ id, ...patch }) => {
        await delay(400);
        const next = mockBackend.updateCampaign(id, patch);
        return next ? { data: next } : { error: { status: 404, data: 'Campaign not found' } };
      },
      invalidatesTags: (result, error, { id }) => [
        { type: 'Campaign', id },
        { type: 'CampaignList', id: 'LIST' },
      ],
    }),

    updateCampaignStatus: builder.mutation({
      // Real: query: ({ id, status }) => buildRequest('updateStatus', { id, status })
      queryFn: async ({ id, status }) => {
        await delay(300);
        const next = mockBackend.updateStatus(id, status);
        return next ? { data: next } : { error: { status: 404, data: 'Campaign not found' } };
      },
      // Optimistic flip of the status pill so the UI feels instant.
      async onQueryStarted({ id, status }, { dispatch, queryFulfilled }) {
        const patchList = dispatch(
          campaignsApi.util.updateQueryData('listCampaigns', undefined, (draft) => {
            const c = draft?.items?.find((x) => x.id === id);
            if (c) c.status = status;
          })
        );
        const patchOne = dispatch(
          campaignsApi.util.updateQueryData('getCampaign', id, (draft) => {
            if (draft) draft.status = status;
          })
        );
        try {
          await queryFulfilled;
        } catch {
          patchList.undo();
          patchOne.undo();
        }
      },
      invalidatesTags: (result, error, { id }) => [
        { type: 'Campaign', id },
        { type: 'CampaignList', id: 'LIST' },
        { type: 'CampaignStats', id: 'SUMMARY' },
      ],
    }),

    sendCampaign: builder.mutation({
      // Real: query: ({ id, sendAt }) => buildRequest('send', { id, sendAt })
      queryFn: async ({ id, sendAt }) => {
        await delay(500);
        const next = mockBackend.sendCampaign(id, sendAt);
        return next ? { data: next } : { error: { status: 404, data: 'Campaign not found' } };
      },
      invalidatesTags: (result, error, { id }) => [
        { type: 'Campaign', id },
        { type: 'CampaignList', id: 'LIST' },
        { type: 'CampaignStats', id: 'SUMMARY' },
      ],
    }),

    testSendCampaign: builder.mutation({
      // Real: query: ({ id, recipients }) => buildRequest('testSend', { id, recipients })
      queryFn: async ({ id, recipients }) => {
        await delay(500);
        return { data: mockBackend.testSend(id, recipients) };
      },
    }),

    duplicateCampaign: builder.mutation({
      // Real: query: (id) => buildRequest('duplicate', { id })
      queryFn: async (id) => {
        await delay(300);
        const next = mockBackend.duplicateCampaign(id);
        return next ? { data: next } : { error: { status: 404, data: 'Campaign not found' } };
      },
      invalidatesTags: [{ type: 'CampaignList', id: 'LIST' }],
    }),

    deleteCampaign: builder.mutation({
      // Real: query: (id) => buildRequest('delete', { id })
      // Backend sets deletedAt (soft delete). Nightly job anonymizes after retention window.
      queryFn: async (id) => {
        await delay(300);
        mockBackend.softDeleteCampaign(id);
        return { data: { success: true } };
      },
      invalidatesTags: [{ type: 'CampaignList', id: 'LIST' }, { type: 'CampaignStats', id: 'SUMMARY' }],
    }),

    restoreCampaign: builder.mutation({
      // Real: query: (id) => buildRequest('restore', { id })
      // Assistant / dark-admin only. Clears deletedAt.
      queryFn: async (id) => {
        await delay(300);
        const next = mockBackend.restoreCampaign(id);
        return next ? { data: next } : { error: { status: 404, data: 'Campaign not found' } };
      },
      invalidatesTags: (result, error, id) => [
        { type: 'Campaign', id },
        { type: 'CampaignList', id: 'LIST' },
      ],
    }),

    hardDeleteCampaign: builder.mutation({
      // Real: query: (id) => buildRequest('hardDelete', { id })
      // Dark-admin only. Audited.
      queryFn: async (id) => {
        await delay(400);
        mockBackend.hardDeleteCampaign(id);
        return { data: { success: true } };
      },
      invalidatesTags: [{ type: 'CampaignList', id: 'LIST' }],
    }),

    exportAudience: builder.mutation({
      // Real: query: ({ filters, format }) => buildRequest('exportAudience', { filters, format })
      queryFn: async (args) => {
        await delay(600);
        return { data: mockBackend.exportAudience(args) };
      },
    }),

    trackUpsellImpression: builder.mutation({
      // Real: query: ({ placementId, campaignId, courseId }) => buildRequest('trackUpsellImpression', {...})
      queryFn: async (data) => {
        await delay(50);
        mockBackend.trackUpsellImpression(data);
        return { data: { ok: true } };
      },
    }),

    trackUpsellClick: builder.mutation({
      // Real: query: ({ placementId, campaignId, courseId }) => buildRequest('trackUpsellClick', {...})
      queryFn: async (data) => {
        await delay(50);
        mockBackend.trackUpsellClick(data);
        return { data: { ok: true } };
      },
    }),
  }),
  overrideExisting: false,
});

export const {
  useListCampaignsQuery,
  useLazyListCampaignsQuery,
  useGetCampaignQuery,
  useLazyGetCampaignQuery,
  useGetCampaignStatsQuery,
  usePreviewAudienceQuery,
  useLazyPreviewAudienceQuery,
  useGetCampaignRecipientsQuery,
  useGetActiveUpsellsQuery,
  useCreateCampaignMutation,
  useUpdateCampaignMutation,
  useUpdateCampaignStatusMutation,
  useSendCampaignMutation,
  useTestSendCampaignMutation,
  useDuplicateCampaignMutation,
  useDeleteCampaignMutation,
  useRestoreCampaignMutation,
  useHardDeleteCampaignMutation,
  useExportAudienceMutation,
  useTrackUpsellImpressionMutation,
  useTrackUpsellClickMutation,
} = campaignsApi;

// Exposed for the few components that still want an RPC request shape directly.
// Prefer the hooks above.
export { buildRequest };
